import { all, call, put, select, takeLatest, takeEvery } from 'redux-saga/effects';
import * as libraryApi from '../api/libraryApi.js';
import { pushDataLayer } from '../../../lib/analytics.js';
import { captureSagaError } from '../../../lib/sentry.js';
import { history } from '../../../lib/history.js';
import {
  libraryRequested,
  catalogReady,
  catalogFailed,
  productDetailRequested,
  productDetailReady,
  productDetailNotFound,
  productDetailFailed,
  couponSubmitted,
  couponApplied,
  couponRejected,
  subscriptionToggleRequested,
  subscriptionToggled,
  paymentStarted,
  gatewayReady,
  paymentFailed,
  androidSubscriptionPurchaseSucceeded,
  androidSubscriptionPurchasePending,
  androidSubscriptionPurchaseCancelled,
  paymentReturnReceived,
  paymentSucceeded,
  paymentAlreadyProcessed,
  paymentRejected,
} from './librarySlice.js';
import { toastShown } from '../../system/store/toastSlice.js';
import { coursesRequested } from '../../courses/store/coursesListSlice.js';
import { ordersRequested } from '../../account/store/accountSlice.js';
import { purchaseSubscription } from '../../../services/native/playBillingService.js';
import {
  isAndroidBillingProduct,
  withAndroidSubscriptionBilling,
  withAndroidSubscriptionBillingList,
} from '../lib/androidBillingProducts.js';
import { isSubscriptionProduct } from '../lib/paymentPricing.js';

// Match legacy's region filter exactly. country: 0 = both, 1 = India-only,
// 2 = international-only. Indian user (default) sees 0 + 1. International
// user (payment_type === 'stripe') sees 0 + 2.
function isInternationalUser() {
  return (localStorage.getItem('payment_type') || '').toLowerCase() === 'stripe';
}

function filterProducts(products) {
  const intl = isInternationalUser();
  return (products ?? []).filter((p) =>
    intl ? p.country === 0 || p.country === 2 : p.country === 0 || p.country === 1,
  );
}

// Wraps a thunk so `all([...])` doesn't abort siblings when one rejects.
function* safeCall(fn) {
  try {
    const value = yield call(fn);
    return { value, error: null };
  } catch (error) {
    captureSagaError(error, { action: 'library/libraryRequested' });
    return { value: null, error };
  }
}

export function* handleLibraryRequested() {
  try {
    const [productsResult, relatedResult] = yield all([
      call(safeCall, libraryApi.fetchProducts),
      call(safeCall, libraryApi.fetchRelatedProducts),
    ]);
    if (productsResult.error || !productsResult.value || !productsResult.value.status || productsResult.value.code !== 200) {
      yield put(catalogFailed(productsResult.error?.message ?? productsResult.value?.message ?? 'Failed to load courses'));
      return;
    }
    // The temporary Android test card is disabled. Real API product IDs are the
    // Google Play subscription product IDs (for example 1007, 1008 and 1009).
    const products = withAndroidSubscriptionBillingList(filterProducts(productsResult.value.data));
    const recommended = (relatedResult.error || !relatedResult.value?.status || relatedResult.value?.code !== 200)
      ? []
      : withAndroidSubscriptionBillingList(filterProducts(relatedResult.value.data));
    yield put(catalogReady({ products, recommended }));
  } catch (err) {
    captureSagaError(err, { action: 'library/libraryRequested' });
    yield put(catalogFailed(err?.message ?? 'Failed to load courses'));
  }
}

export function* handleProductDetailRequested(action) {
  try {
    const res = yield call(libraryApi.fetchProductDetails, action.payload);
    if (!res.status || res.code === 404) {
      yield put(productDetailNotFound());
      return;
    }
    if (res.code !== 200) {
      yield put(productDetailFailed(res.message ?? 'Failed to load product'));
      return;
    }
    yield put(productDetailReady(withAndroidSubscriptionBilling(res.data)));
  } catch (err) {
    captureSagaError(err, { action: 'library/productDetailRequested' });
    yield put(productDetailFailed(err?.message ?? 'Failed to load product'));
  }
}

export function* handleCouponSubmitted() {
  const { currentProductId, couponCode } = yield select((s) => s.library);
  if (!couponCode || !currentProductId) {
    yield put(couponRejected('Enter a coupon code'));
    return;
  }
  try {
    const res = yield call(libraryApi.validateCoupon, couponCode, currentProductId);
    if (!res.status || res.code !== 200 || res.data?.result !== 'valid') {
      yield put(couponRejected(res.message ?? 'Invalid coupon'));
      return;
    }
    yield put(couponApplied({
      discountedPrice: res.data.discounted_price ?? null,
    }));
  } catch (err) {
    captureSagaError(err, { action: 'library/couponSubmitted' });
    yield put(couponRejected(err?.message ?? 'Coupon validation failed'));
  }
}

// Handles subscriptionToggleRequested (NOT subscriptionToggled directly).
// We read couponStatus here — before the reducer clears it — then commit
// the toggle via subscriptionToggled at the end.
export function* handleSubscriptionToggleRequested(action) {
  const { couponCode, couponStatus } = yield select((s) => s.library);
  if (couponStatus === 'applied' && couponCode) {
    // Commit the toggle first so the price tier is correct when revalidating.
    yield put(subscriptionToggled(action.payload));
    yield put(couponSubmitted());
  } else {
    yield put(subscriptionToggled(action.payload));
  }
}

function getStoredPaymentCoupon() {
  return localStorage.getItem('CouponCode') || localStorage.getItem('gtm_cpn_code') || '';
}

function persistPaymentStartContext(productDetail, productId, couponCode, gatewayData = {}, isSubscription = false) {
  localStorage.setItem('currentProductId', String(productId || ''));
  localStorage.setItem('gtm_prod_name', productDetail?.product_name || '');
  localStorage.setItem('gtm_cpn_code', couponCode || '');
  localStorage.setItem('gtm_prod_price', String(productDetail?.offer_price ?? ''));
  localStorage.setItem('productNav', productDetail?.product_name_slug || productDetail?.product_slug || '');
  localStorage.setItem('is_subscription', isSubscription ? '1' : '0');
  if (gatewayData?.inr_price !== undefined && gatewayData?.inr_price !== null) {
    localStorage.setItem('gtm_prod_final', String(Math.floor(Number(gatewayData.inr_price))));
  }
  if (gatewayData?.isChildCourse !== undefined) localStorage.setItem('isChildCourse', String(gatewayData.isChildCourse));
  if (gatewayData?.payment_type) localStorage.setItem('payment_type', String(gatewayData.payment_type));
  if (gatewayData?.txnToken) localStorage.setItem('txnToken', String(gatewayData.txnToken));
  if (gatewayData?.sub_action_url) localStorage.setItem('sub_action_url', String(gatewayData.sub_action_url));
  if (gatewayData?.params?.ORDER_ID) localStorage.setItem('orderId', String(gatewayData.params.ORDER_ID));
}

function clearPaymentInProcessFlag() {
  localStorage.removeItem('TriggerBuyNow');
  localStorage.removeItem('paymentInProcess');
}

function selectObfuscatedAccountId(state) {
  const user = state.auth?.user ?? {};
  const accountId = user.user_id
    ?? user.id
    ?? user.login_id
    ?? localStorage.getItem('login_user_id')
    ?? null;
  return accountId ? `step-${accountId}` : null;
}

function isUserCancelledBillingError(err) {
  return err?.code === 'USER_CANCELED' || /user canceled/i.test(err?.message ?? '');
}

function getAndroidBillingErrorMessage(err) {
  if (err?.code === 'DEVELOPER_ERROR' || err?.code === 'BILLING_5' || /signed correctly/i.test(err?.message ?? '')) {
    return 'Google Play says this app build is not signed/configured for billing. Upload a signed release build to internal testing, publish it, then install that same version from the Play Store testing link.';
  }
  if (err?.code === 'BILLING_UNAVAILABLE' || /billing api version .*not supported/i.test(err?.message ?? '')) {
    return 'Google Play Billing is unavailable on this device. Update or enable the Play Store, then install the internal testing app from Google Play using a tester account.';
  }
  if (err?.code === 'SERVICE_DISCONNECTED') {
    return 'Could not connect to Google Play Billing. Open or update the Play Store, then try again.';
  }
  if (err?.code === 'ITEM_UNAVAILABLE') {
    return 'This Google Play subscription is not available yet. Check that the product and its base plan are active for this app and region in Play Console.';
  }
  return err?.message ?? 'Google Play purchase could not be started.';
}

export function* handleAndroidSubscriptionPayment(lib) {
  const billing = lib.productDetail?.android_billing;
  try {
    const obfuscatedAccountId = yield select(selectObfuscatedAccountId);
    if (!obfuscatedAccountId) {
      throw new Error('Could not identify the signed-in STEP account.');
    }
    const result = yield call(purchaseSubscription, {
      productId: billing.product_id,
      basePlanId: billing.base_plan_id,
      offerId: billing.offer_id ?? null,
      obfuscatedAccountId,
    });

    if (result?.purchase?.purchaseState !== 1) {
      yield put(androidSubscriptionPurchasePending(result));
      yield put(toastShown({
        variant: 'info',
        message: 'Your Google Play payment is pending. Access will be enabled after Google confirms payment.',
      }));
      return;
    }

    yield put(androidSubscriptionPurchaseSucceeded(result));
    yield put(toastShown({
      variant: 'success',
      message: 'Google Play purchase completed. Refreshing your courses.',
    }));
    yield put(coursesRequested());
    yield put(ordersRequested());
    history.push('/courses');
  } catch (err) {
    if (isUserCancelledBillingError(err)) {
      yield put(androidSubscriptionPurchaseCancelled());
      return;
    }
    const message = getAndroidBillingErrorMessage(err);
    captureSagaError(err, { action: 'library/androidSubscriptionPayment' });
    yield put(paymentFailed(message));
    yield put(toastShown({
      variant: 'error',
      message,
    }));
  }
}

export function* handlePaymentStarted() {
  const lib = yield select((s) => s.library);
  const productDetail = lib.productDetail;
  if (!productDetail) {
    yield put(paymentFailed('No product loaded'));
    return;
  }

  if (isAndroidBillingProduct(productDetail)) {
    yield call(handleAndroidSubscriptionPayment, lib);
    return;
  }

  // Web gateway path only. Android subscriptions return above and never send this payload.
  const isSubscription = isSubscriptionProduct(productDetail);
  const body = {
    product_id: lib.currentProductId,
    is_mobile: 'no',
    coupon: lib.couponCode || getStoredPaymentCoupon(),
    is_subscription: isSubscription ? 1 : 0,
    payment_method: localStorage.getItem('payment_method') || 'DC',
    payment_type: localStorage.getItem('payment_type') || 'paytm',
  };
  try {
    const res = yield call(libraryApi.startPayment, body);
    if (!res.status || res.code !== 200) {
      yield put(paymentFailed(res.message ?? 'Payment could not be started'));
      return;
    }
    pushDataLayer({
      event: 'purchase_started',
      product_id: lib.currentProductId,
      product_name: productDetail.product_name,
      coupon_code: lib.couponCode || '',
      actual_product_value: productDetail.offer_price,
    });
    yield call(persistPaymentStartContext, productDetail, lib.currentProductId, body.coupon, res.data, isSubscription);
    yield put(gatewayReady(res.data));
  } catch (err) {
    captureSagaError(err, { action: 'library/paymentStarted' });
    yield put(paymentFailed(err?.message ?? 'Payment could not be started'));
    yield put(toastShown({
      variant: 'error',
      message: 'Payment could not be started. Please try again.',
    }));
  }
}

export function* handlePaymentReturnReceived(action) {
  const queryString = action.payload || '';
  const params = new URLSearchParams(queryString);
  const orderId = params.get('ORDERID') || params.get('order_id') || params.get('orderid') || '';
  const paymentType = localStorage.getItem('payment_type') || 'paytm';

  try {
    const logRes = yield call(libraryApi.logPayment, queryString);
    if (!logRes.status) {
      pushDataLayer({ event: 'purchase_failure' });
      yield put(paymentRejected());
      return;
    }
    const procRes = yield call(libraryApi.processPayment, orderId, paymentType);
    const alreadyProcessed = procRes.message === 'Payment already processed';
    if (alreadyProcessed) {
      // Already-processed means the gateway settled earlier; the user is already
      // entitled. No GTM event (this isn't a new conversion). PaymentProcessPage
      // navigates to /courses on this state.
      yield call(clearPaymentInProcessFlag);
      yield put(ordersRequested());
      yield put(paymentAlreadyProcessed());
      return;
    }
    if (procRes.status && procRes.code === 200) {
      pushDataLayer({ event: 'purchase_success', order_id: orderId });
      yield call(clearPaymentInProcessFlag);
      yield put(ordersRequested());
      yield put(paymentSucceeded());
      return;
    }
    // Reached only when procRes.status is falsy and it's not the already-processed case.
    pushDataLayer({ event: 'purchase_failure', order_id: orderId });
    yield call(clearPaymentInProcessFlag);
    yield put(paymentRejected());
  } catch (err) {
    captureSagaError(err, { action: 'library/paymentReturnReceived' });
    pushDataLayer({ event: 'purchase_failure' });
    yield call(clearPaymentInProcessFlag);
    yield put(paymentRejected());
    yield put(toastShown({
      variant: 'error',
      message: "We couldn't confirm your payment status. Please check your order history or contact support.",
    }));
  }
}

export default function* librarySaga() {
  yield takeLatest(libraryRequested.type, handleLibraryRequested);
  yield takeLatest(productDetailRequested.type, handleProductDetailRequested);
  yield takeLatest(couponSubmitted.type, handleCouponSubmitted);
  yield takeEvery(subscriptionToggleRequested.type, handleSubscriptionToggleRequested);
  yield takeLatest(paymentStarted.type, handlePaymentStarted);
  yield takeEvery(paymentReturnReceived.type, handlePaymentReturnReceived);
}
